import { api } from "./../api";
import { ApiDynamicPaths, ApiPaths } from "./../api/paths";
import { ICreateFormRequestData, IForm, ISaveChangedFontsRequestData } from "./../../store/forms/types";
import {
    GetFormsResponse,
    GetFormResponse,
    UpdateFormResponse,
    SaveUserFontsResponse,
    UpdatePaymentSettingsResponse,
    IUpdatePaymentSettings,
    CreatePaymentPromoCodeResponse,
    UpdatePaymentPromoCodeResponse,
    DuplicatePaymentPromoCodeResponse,
    DeletePaymentPromoCodeResponse,
    GetPaymentPromoCodeListResponse,
    GetPaymentStripeAuthURLResponse,
    UpdatePaymentStripeAuthURLResponse,
    UpdatePaymentPayPalAuthResponse,
    GetSquareSubscriptionsResponse,
    GetTransferFormResponce,
    GetZapierAuthURLResponse,
    HubspotAuthURLResponse,
    GetHubspotDataResponse,
    GetHubspotStatusResponse,
    UpdateHubspotPropertyResponse,
    UpdateHubspotPropertyRequest,
    GetHubspotTablesRequest,
    GetHubspotTablesResponse,
    InitHubspotResponse,
    DisconnectIntegrationResponse,
} from "./forms.service.d";
import { getFormIdFromUrl } from "./../../helpers/getUrlParams";

export class FormsService {
    /**
     * Get Wix form Transfer data
     *
     * @param instance {String}
     * @param compId {String}
     */
    static async transferForm(instance: string, compId: string): Promise<GetTransferFormResponce> {
        return api.post(ApiDynamicPaths({ instance: instance, compId: compId }).TRANSFER_FORM);
    }
    /**
     * Transfer Wix Submissions request
     *
     * @param instance {String}
     * @param compId {String}
     * @param formId {String}
     * @param fieldsDictionary {{[key:number]:string}}
     */
    static async transferSubmissions(
        instance: string,
        compId: string,
        formId: string,
        fieldsDictionary: { [key: number]: string },
    ): Promise<GetTransferFormResponce> {
        return api.post(ApiDynamicPaths({ instance: instance, compId: compId, form_id: formId }).TRANSFER_SUBMISSIONS, {
            wixToPlatformFields: fieldsDictionary,
        });
    }
    /**
     * Get All Forms request
     *
     * @param  no_params
     */
    static async getForms(): Promise<GetFormsResponse> {
        return api.get(ApiPaths.GET_FORMS);
    }

    /**
     * Get All Forms request
     *
     * @param
     */
    static async getForm(id: IForm["_id"]): Promise<GetFormResponse> {
        return api.get(ApiPaths.GET_FORMS + "/" + id);
    }

    /**
     * Create a new form request
     *
     * @param  {ICreateFormRequestData} data
     */
    static async createForm(data: ICreateFormRequestData): Promise<GetFormsResponse> {
        return api.post(ApiPaths.GET_FORMS, data);
    }

    /**
     * Update form request
     *
     * @param  {IForm} data
     */
    static async updateForm(data: IForm): Promise<UpdateFormResponse> {
        return api.put(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).UPDATE_FORM, data);
    }

    static async newUpdateForm(data: any): Promise<UpdateFormResponse> {
        return api.put(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).UPDATE_FORM, data);
    }

    /**
     * Update form request
     *
     * @param  {T} data
     */
    static async updateFormComponent<T>(data: T): Promise<UpdateFormResponse> {
        return api.put(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).UPDATE_FORM, data);
    }

    /**
     * Get User Fonts request
     *
     * @param  no_params
     */
    static async getUserFonts(): Promise<SaveUserFontsResponse> {
        return api.get(ApiPaths.GET_USER_FONTS);
    }

    /**
     * Set User Fonts request
     *
     * @param  no_params
     */
    static async saveUserFonts(data: ISaveChangedFontsRequestData): Promise<SaveUserFontsResponse> {
        return api.post(ApiPaths.ADD_USER_FONT, data);
    }

    //Payment

    /**
     * Update Payment Settings
     *
     * @param  data: ISetPaymentOption
     */
    static async updatePaymentSettings({ data }: IUpdatePaymentSettings): Promise<UpdatePaymentSettingsResponse> {
        return api.put(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).UPDATE_PAYMENT_SETTINGS, data);
    }

    /**
     * Create Payment PromoCode
     *
     * @param  no_params
     */
    static async createPaymentPromoCode(data: any): Promise<CreatePaymentPromoCodeResponse> {
        return api.post(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).CREATE_PAYMENT_PROMOCODE, data);
    }

    /**
     * Update Payment PromoCode
     *
     * @param  {promoCode id} id , {promoCode data}  data
     */
    static async updatePaymentPromoCode(data: any, id: string): Promise<UpdatePaymentPromoCodeResponse> {
        return api.put(ApiDynamicPaths({ promocode_id: id }).UPDATE_PAYMENT_PROMOCODE, data);
    }
    /**
     * Duplicate Payment PromoCode
     *
     * @param  {promoCode id} id
     */
    static async duplicatePaymentPromoCode(id: any): Promise<DuplicatePaymentPromoCodeResponse> {
        return api.put(ApiDynamicPaths({ promocode_id: id }).DUPLICATE_PAYMENT_PROMOCODE);
    }

    /**
     * Delete Payment PromoCode
     *
     * @param  {promoCode id} id
     */
    static async deletePaymentPromoCode(id: any): Promise<DeletePaymentPromoCodeResponse> {
        return api.delete(ApiDynamicPaths({ promocode_id: id }).DELETE_PAYMENT_PROMOCODE);
    }

    /**
     * Get PromoCodes List
     *
     * @param  id : paymentSettingsId
     */
    static async getPaymentPromoCodesList(data: any): Promise<GetPaymentPromoCodeListResponse> {
        return api.get(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).GET_PAYMENT_PROMOCODES_LIST);
    }

    /**
     * Get STRIPE Auth URL
     *
     * @param  no_params
     */
    static async getPaymentStripeAuthURL(): Promise<GetPaymentStripeAuthURLResponse> {
        return api.get(ApiDynamicPaths({}).GET_PAYMENT_STRIPE_AUTH_URL);
    }
    /**
     * Get SQUARE Auth URL
     *
     * @param  no_params
     */
    static async getPaymentSquareAuthURL(): Promise<GetPaymentStripeAuthURLResponse> {
        return api.get(ApiDynamicPaths({}).GET_PAYMENT_SQUARE_AUTH_URL);
    }
    /**
     * Get SQUARE Auth URL
     *
     * @param paymentSettingsId {String}
     */
    static async getSquareSubscriptions(paymentSettingsId: string): Promise<GetSquareSubscriptionsResponse> {
        return api.get(ApiDynamicPaths({ payment_settings_id: paymentSettingsId }).GET_SQUARE_SUBSCRIPTION_PLANS);
    }
    /**
     * Update STRIPE Auth
     *
     * @params  formId : string , code : string , scope : string
     */
    static async updatePaymentStripeAuth(data: { queries: string }): Promise<UpdatePaymentStripeAuthURLResponse> {
        return api.get(ApiDynamicPaths({ queries: data.queries }).UPDATE_PAYMENT_STRIPE_AUTH);
    }
    /**
     * Activate Cash Payment
     *
     * @param  no_params
     */
    static async activateCashPayment(): Promise<UpdatePaymentStripeAuthURLResponse> {
        return api.put(ApiDynamicPaths({}).ACTIVATE_PAYMENT_CASH, { formId: getFormIdFromUrl() });
    }

    /**
     * Update PayPal Auth
     *
     * @param  {formID} formID , {paypal email} email
     */
    static async updatePaymentPayPalAuth(data: any): Promise<UpdatePaymentPayPalAuthResponse> {
        return api.post(ApiDynamicPaths({}).UPDATE_PAYMENT_PAYPAL_AUTH, data);
    }

    /**
     * Disconnect payment method
     *
     * @param  {type} PaymentMethodEnum
     */
    static async disconnectPaymentMethod(data: { type: any }): Promise<UpdatePaymentPayPalAuthResponse> {
        return api.put(ApiDynamicPaths({}).DISCONNECT_PAYMENT_METHOD, {
            formId: getFormIdFromUrl(),
            type: data.type,
        });
    }
    /**
     * Get Zapier Auth URL
     *
     */
    static async getZapierAuthURL(formId: string): Promise<GetZapierAuthURLResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).ZAPIER_AUTH_URL);
    }
    /**
     * Disconnect an integration
     *
     * @param type {number}
     */
    static async disconnectIntegration(type: string): Promise<DisconnectIntegrationResponse> {
        return api.put(ApiDynamicPaths({ form_id: getFormIdFromUrl() }).DISCONNECT_INTEGRATION, {
            type,
        });
    }

    /**
     * Get Hubspot Auth URL
     *
     */

    static async getHubspotAuthURL(formId: string): Promise<HubspotAuthURLResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).HUBSPOT_AUTH_URL);
    }

    /**
     * Get Hubspot Data
     *
     */

    static async getHubspotData(formId: string): Promise<GetHubspotDataResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_HUBSPOT_DATA);
    }

    /**
     * Get Hubspot Status
     *
     */
    static async getHubspotStatus(formId: string): Promise<GetHubspotStatusResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).GET_HUBSPOT_STATUS);
    }

    /**
     * Update Hubspot Property
     *
     */
    static async updateHubspotProperty(
        formId: string,
        data: UpdateHubspotPropertyRequest,
    ): Promise<UpdateHubspotPropertyResponse> {
        return api.put(ApiDynamicPaths({ form_id: formId }).UPDATE_HUBSPOT_PROPERTY, data);
    }

    /**
     * Get Hubspot Tables
     *
     */
    static async getHubspotTables(formId: string, table: GetHubspotTablesRequest): Promise<GetHubspotTablesResponse> {
        return api.get(`${ApiDynamicPaths({ form_id: formId }).GET_HUBSPOT_TABLES}?table=${table.table}`);
    }
    /**
     * Get Hubspot Auth URL
     *
     */
    static async initHubspot(formId: string): Promise<InitHubspotResponse> {
        return api.get(ApiDynamicPaths({ form_id: formId }).HUBSPOT_INIT);
    }
}
